/**
 * URL state — the single registry of the query parameters and deep-link paths
 * this app navigates by.
 *
 * `MainContent.tsx` and `App.tsx` both read and write the same address bar, and
 * each used to carry its own hand-maintained list of parameters: the router knew
 * about `estimateId`, `expenseId`, `movementId`, `movementItemId` and
 * `serialNo`; `App.tsx` knew about `rateId` and `clientCode`; neither knew about
 * the other's. A parameter added to one was silently dropped by the other.
 *
 * `LOCATION_PARAMS` and the two path maps below are now the only place either
 * of those lists exists. Adding a navigable parameter means adding it here,
 * once.
 *
 * Note `App.tsx` still owns `parseUrl()` and its legacy `#/hash-path` map. That
 * is deliberate scope: it is a separate concern from the parameter registry,
 * and `App.tsx` has its own entry in `docs/development/code-review-plan.md` §6.
 */

/**
 * Every identifier the app carries in the query string. `view` and `cat` are
 * handled separately — they select the screen rather than the record on it.
 */
export const LOCATION_PARAMS = [
  'id',
  'requestId',
  'invoiceId',
  'jobId',
  'rateId',
  'clientCode',
  'estimateId',
  'expenseId',
  'movementId',
  'movementItemId',
  'serialNo',
] as const;

export type LocationParam = (typeof LOCATION_PARAMS)[number];

/** The id parameters of a location, all optional. */
export type LocationIds = { [K in LocationParam]?: string | null };

/** A fully-described location: which screen, which category, which record. */
export interface LocationState extends LocationIds {
  view: string | null;
  cat: string | null;
}

export interface WriteLocationOptions {
  /** `true` pushes a new history entry, `false` replaces the current one. */
  push?: boolean;
}

/**
 * Paths that resolve to a view when there is no `?view=` in the URL.
 *
 * This is deliberately NOT the exact inverse of `VIEW_TO_PATH` below: `/` and
 * `/home` both read as Home, but Home is *written* as `/?view=Home` so that
 * navigating home leaves an unambiguous history entry.
 */
export const PATH_TO_VIEW: Readonly<Record<string, string>> = {
  '/': 'Home',
  '/home': 'Home',
  '/client-invoices': 'ClientInvoiceList',
  '/procurement/estimates': 'ProcurementEstimatesList',
  '/procurement/requests': 'ClientRequestList',
  '/clients': 'ClientsList',
  '/hr/self-service/payslips': 'HRPayslips',
};

/** Views that own a real path rather than a `?view=` query parameter. */
export const VIEW_TO_PATH: Readonly<Record<string, string>> = {
  ClientInvoiceList: '/client-invoices',
  ProcurementEstimatesList: '/procurement/estimates',
  ClientRequestList: '/procurement/requests',
  ClientsList: '/clients',
  HRPayslips: '/hr/self-service/payslips',
};

const EMPTY_LOCATION: LocationState = { view: null, cat: null };

/**
 * The Approval Desk's sections, as a `tab` deep link names them: the approval
 * queues (which `approvalsTab` picks between), then the person's own to-dos —
 * Training, Due to receipt and Card expenses (corporate-card transactions
 * waiting on them for a receipt or coding). The desk shows them as one strip.
 * A `?view=Home` link carrying one of these is an approvals deep link from
 * before the Home / Approval Desk split.
 */
export const APPROVALS_TABS = ['approvals', 'training', 'receipting', 'cards'] as const;
export type ApprovalsTab = (typeof APPROVALS_TABS)[number];

export function isApprovalsTab(v: string | null | undefined): v is ApprovalsTab {
  return !!v && (APPROVALS_TABS as readonly string[]).includes(v);
}

/**
 * Tabs the desk no longer has, and the tab each opens instead. Their names are
 * in emails already sent (the digest footer linked to `tab=action-items`), so
 * they must keep opening the desk. Work Items' approvals moved into Approvals.
 * Performance Alerts (probation reviews) left the desk on 2026-10-19: managers
 * are told by the Monday email, and HR works from HR → Performance.
 */
const RETIRED_APPROVALS_TABS: Readonly<Record<string, ApprovalsTab>> = {
  'action-items': 'approvals',
  'work-items': 'approvals',
  performance: 'approvals',
};

/** The desk tab a `tab` URL value opens, retired names included; null if none. */
export function approvalsTabFrom(v: string | null | undefined): ApprovalsTab | null {
  if (!v) return null;
  if (isApprovalsTab(v)) return v;
  return Object.prototype.hasOwnProperty.call(RETIRED_APPROVALS_TABS, v) ? RETIRED_APPROVALS_TABS[v] : null;
}

/**
 * Forwards pre-split approvals links to the queue's new route.
 *
 * The approvals queue used to BE the home page, so every approval email this
 * system has sent points at `?view=Home&tab=approvals&...`. Those messages are
 * already in inboxes and cannot be recalled, so the link must keep working --
 * which is also why the backend templates still emit the old form.
 *
 * Deliberately narrow: a bare `?view=Home`, or one with an unrelated `tab`, is
 * the landing page and is NOT redirected. Applied by both `readLocation()` and
 * `parseUrl()` so the router and `App.tsx` cannot disagree about it.
 */
export function resolveLegacyHomeView(view: string | null, tab: string | null): string | null {
  if (view === 'Home' && approvalsTabFrom(tab)) return 'ApprovalsDesk';
  return view;
}

/**
 * Names for the screens that carry no heading of their own -- the top bar and
 * sidebar were meant to say where you are, but below `xl` the sidebar is a
 * closed drawer, so the top bar shows this name there instead.
 */
const SCREEN_NAMES: Readonly<Record<string, string>> = {
  Home: 'Home',
  ApprovalsDesk: 'Approval Desk',
  TimesheetPage: 'Timesheets',
  Leave: 'Leave',
  ExpensesDashboard: 'Expenses',
};

export function screenNameFor(view: string | null | undefined): string | null {
  return view && Object.prototype.hasOwnProperty.call(SCREEN_NAMES, view) ? SCREEN_NAMES[view] : null;
}

/**
 * The Approval Desk URL, optionally opened on one queue (`expenses`,
 * `timesheets`, `leave`, `invoices`, `purchaseOrders`).
 *
 * `tab` and `approvalsTab` are read by `ApprovalsDesk` at mount (and again on
 * the `navigate-home` event). They are deliberately not in `LOCATION_PARAMS`:
 * they select a panel on one screen, and carrying them through `writeLocation`
 * would drag `tab=approvals` onto every screen navigated to afterwards.
 */
export function approvalsDeskUrl(approvalsTab?: string): string {
  const params = new URLSearchParams({ view: 'ApprovalsDesk' });
  if (approvalsTab) {
    params.set('tab', 'approvals');
    params.set('approvalsTab', approvalsTab);
  }
  return `/?${params.toString()}`;
}

/** Which queue a record opened on the desk is going back to (approvalsDeskReturnUrl). */
const DESK_RETURN_KEY = 'grautomates/approvals-desk/return';

/**
 * `approvalsDeskUrl`, for going back to a queue from a record opened on it.
 * The queue then reopens on the period it was showing (Expenses' Past); every
 * other way in -- the queue's card, an email link, the sidebar -- opens it on
 * Current. Marked in the session, not the URL, so a link never carries it.
 */
export function approvalsDeskReturnUrl(approvalsTab: string): string {
  try {
    sessionStorage.setItem(DESK_RETURN_KEY, approvalsTab);
  } catch {
    // No storage (private mode): the queue opens on Current.
  }
  return approvalsDeskUrl(approvalsTab);
}

/** Whether the desk is opening as a return to this queue (approvalsDeskReturnUrl). */
export function isApprovalsDeskReturn(approvalsTab: string): boolean {
  try {
    return sessionStorage.getItem(DESK_RETURN_KEY) === approvalsTab;
  } catch {
    return false;
  }
}

/** Spends the return once the queue has opened: the next way in is not one. */
export function endApprovalsDeskReturn(): void {
  try {
    sessionStorage.removeItem(DESK_RETURN_KEY);
  } catch {
    // Nothing was stored.
  }
}

/** An employee's HR page (HR → Employees → the person). */
export function hrEmployeeUrl(employeeId: string): string {
  return `/?${new URLSearchParams({ view: 'HREmployeeDetail', id: employeeId }).toString()}`;
}

/**
 * Expenses → Corporate → Not Submitted: where a card holder matches a receipt
 * and codes their own card transactions. The bell links here too
 * (backend/services/actionItems/cardSubmissions.ts, CARD_MATCHING_HREF).
 */
export const CARD_MATCHING_URL = '/?view=ExpensesDashboard&cat=Expenses&tab=corporate&status=unmatched';

/** The desk opened on one of its other tabs (receipting, training, cards). */
export function approvalsDeskTabUrl(tab: ApprovalsTab): string {
  return `/?${new URLSearchParams({ view: 'ApprovalsDesk', tab }).toString()}`;
}

/**
 * The Approval Desk as a panel on another site's page (SharePoint frames
 * `/?view=ApprovalsDesk&embed=1`): the card grid only, each card opening the
 * desk in a new tab, signed in silently through `/auth/azure/start` (backend
 * routes/embedAuth.ts). `embed=1` is read live, never carried by
 * writeLocation — a panel never navigates inside its own frame. `embedSignin`
 * is the sign-in's answer when it could not issue a session, so the panel can
 * say why instead of trying again forever.
 */
export const EMBED_PARAM = 'embed';
export const EMBED_SIGNIN_PARAM = 'embedSignin';
export const EMBED_SIGNIN_OUTCOMES = ['needed', 'no-account', 'failed', 'unavailable'] as const;
export type EmbedSigninOutcome = (typeof EMBED_SIGNIN_OUTCOMES)[number];

/** Leave for `href` without a history entry. The seam a test stands in for. */
export function replaceLocation(href: string): void {
  window.location.replace(href);
}

export function isEmbeddedPanel(search: string = window.location.search): boolean {
  return new URLSearchParams(search).get(EMBED_PARAM) === '1';
}

export function readEmbedSignin(search: string = window.location.search): EmbedSigninOutcome | null {
  const v = new URLSearchParams(search).get(EMBED_SIGNIN_PARAM);
  return (EMBED_SIGNIN_OUTCOMES as readonly string[]).includes(v || '') ? (v as EmbedSigninOutcome) : null;
}

/**
 * Home, scrolled to one announcement — the link the bell's "Active warnings"
 * uses. The backend builds the same URL (homePostHref in
 * services/actionItems/activeWarnings.ts); change the two together.
 *
 * `post` is read by Home at mount and on navigation, like the desk's `tab`,
 * and is not in LOCATION_PARAMS for the same reason: it points at a place on
 * one screen, and writeLocation would carry it onto every screen after.
 */
export const HOME_POST_PARAM = 'post';

export function homePostUrl(announcementId: string): string {
  return `/?${new URLSearchParams({ view: 'Home', [HOME_POST_PARAM]: announcementId }).toString()}`;
}

/** The element id Home gives each post, which a `post` link scrolls to. */
export function homePostAnchorId(announcementId: string): string {
  return `update-${announcementId}`;
}

/** The announcement the current URL asks Home to show, or null. Only a real id is accepted. */
export function readHomePostParam(): string | null {
  if (typeof window === 'undefined') return null;
  const v = (new URLSearchParams(window.location.search || '').get(HOME_POST_PARAM) || '').trim();
  return /^[0-9a-f]{24}$/i.test(v) ? v : null;
}

/**
 * Home opened on one shoutout: `/?view=Home&shoutout=<id>` — the link the
 * notification bell carries (built by `shoutoutHref` in
 * backend/services/shoutouts.ts; the parameter name must match). Like `tab`,
 * it selects something on one screen and is deliberately not in
 * `LOCATION_PARAMS`, so it is not carried onto the next screen.
 */
export const SHOUTOUT_PARAM = 'shoutout';

/**
 * The shoutout the address asks Home to show, or null. Read once and then
 * cleared with `clearShoutoutParam`, so a stale link is not re-applied on every
 * later navigation.
 */
export function readShoutoutParam(): string | null {
  if (typeof window === 'undefined') return null;
  const id = new URLSearchParams(window.location.search || '').get(SHOUTOUT_PARAM);
  return id && /^[a-f0-9]{24}$/i.test(id) ? id : null;
}

/** Drop the shoutout parameter from the address, in place (no history entry). */
export function clearShoutoutParam(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has(SHOUTOUT_PARAM)) return;
  url.searchParams.delete(SHOUTOUT_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

/**
 * Home, showing one month of company updates — optionally scrolled to a post
 * in it. `monthKey` is the server's `YYYY-MM` key, taken as given: the month
 * buckets are defined once, in backend/services/homepageArchive.ts, and the
 * browser never works one out. No key is the default view (this month).
 *
 * Like `post`, `month` is read by Home and kept out of LOCATION_PARAMS, so it
 * never follows a person onto another screen.
 */
export const HOME_MONTH_PARAM = 'month';
/** Home used to browse by week (`week=YYYY-MM-W`); links already shared still carry it. */
const LEGACY_WEEK_PARAM = 'week';

export function homeMonthUrl(monthKey?: string | null, postId?: string | null): string {
  const q = new URLSearchParams({ view: 'Home' });
  if (monthKey) q.set(HOME_MONTH_PARAM, monthKey);
  if (postId) q.set(HOME_POST_PARAM, postId);
  return `/?${q.toString()}`;
}

/**
 * The month the current URL asks Home to show, or null for the default. An
 * old `week=YYYY-MM-W` link opens the month that week belonged to.
 */
export function readHomeMonthParam(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search || '');
  const month = (params.get(HOME_MONTH_PARAM) || '').trim();
  if (month) return month;
  const legacy = /^(\d{4}-\d{2})-[1-4]$/.exec((params.get(LEGACY_WEEK_PARAM) || '').trim());
  return legacy ? legacy[1] : null;
}

/**
 * Go to an in-app relative URL without a page load.
 *
 * Pushes the URL and lets the app's own listeners read it: App and MainContent
 * both route on `popstate`, and the Approval Desk re-reads its tab parameters
 * on `navigate-home` when it is already mounted. The caller is responsible for
 * passing a SAFE relative href (see components/home/safeUrl.ts).
 */
export function navigateToHref(href: string): void {
  if (typeof window === 'undefined') return;
  window.history.pushState({}, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
  window.dispatchEvent(new Event('navigate-home'));
}

/**
 * True when the address names no screen at all — the bare `/` a user reaches
 * after signing in or typing the site's address.
 *
 * Only this case is subject to `resolveDefaultView`. Anything the URL does say
 * wins: `?view=Home` is an explicit request for the landing page, `/home` is a
 * typed path, and `/?tab=approvals` is a pre-split approvals link that
 * `resolveLegacyHomeView` already forwards.
 */
export function urlNamesNoView(): boolean {
  if (typeof window === 'undefined') return false;
  const { pathname, search, hash } = window.location;
  if (pathname !== '/') return false;
  if (hash && hash !== '#') return false;
  const query = new URLSearchParams(search || '');
  return !query.has('view') && !query.has('tab');
}

/**
 * Where a URL that names no view should land. A saved choice (top bar ->
 * Settings) wins, except that the Approval Desk is honoured only while the
 * person still approves anything; without a choice, approvers land on their
 * queue and everyone else on Home. `isApprover` comes from
 * `GET /api/home/landing` (see `hooks/useLandingProfile.ts`): whether the
 * person may open the Approval Desk, which the server grants to approvers.
 */
export function resolveDefaultView(
  isApprover: boolean,
  preferred: 'home' | 'approvals' | null = null
): 'ApprovalsDesk' | 'Home' {
  if (preferred === 'home') return 'Home';
  return isApprover ? 'ApprovalsDesk' : 'Home';
}

/**
 * Read the current location from the URL.
 *
 * Precedence: query string, then `#view=` (back-compat for hand-typed hashes),
 * then a deep-link path. Returns `view: null` when the URL names no view, which
 * the caller treats as "leave the current screen alone".
 */
export function readLocation(): LocationState {
  if (typeof window === 'undefined') return { ...EMPTY_LOCATION };

  const query = new URLSearchParams(window.location.search || '');

  const state: LocationState = {
    view: query.get('view'),
    cat: query.get('cat'),
  };
  LOCATION_PARAMS.forEach(param => {
    state[param] = query.get(param);
  });

  // Back-compat: only when the query names no view is the hash consulted, and
  // then a hash value wins per-parameter while the query still supplies
  // anything the hash omits — `/?id=123#view=Foo` keeps both halves.
  let tab = query.get('tab');
  if (!state.view) {
    const hash = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
    state.view = hash.get('view');
    tab = hash.get('tab') || tab;
    state.cat = hash.get('cat') || state.cat;
    LOCATION_PARAMS.forEach(param => {
      state[param] = hash.get(param) || state[param];
    });
  }

  if (!state.view) {
    state.view = PATH_TO_VIEW[window.location.pathname] ?? null;
  }

  state.view = resolveLegacyHomeView(state.view, tab);

  // Expense links were sent as `?view=ExpenseDetail&id=X` for a while, but the
  // screen reads `expenseId`. Those emails cannot be recalled, so `id` stands
  // in for `expenseId` on this view when `expenseId` itself is absent.
  if (state.view === 'ExpenseDetail' && !state.expenseId && state.id) {
    state.expenseId = state.id;
  }
  return state;
}

/**
 * Write a location to the URL.
 *
 * The URL is rebuilt from `/` rather than edited in place, so a parameter left
 * out of `state` is dropped rather than inherited from the previous screen —
 * which is what stops an invoice id following the user onto a job detail page.
 */
export function writeLocation(
  state: LocationState,
  { push = true }: WriteLocationOptions = {}
): void {
  if (typeof window === 'undefined') return;

  const url = new URL('/', window.location.origin);
  if (state.view) url.searchParams.set('view', state.view);
  if (state.cat) url.searchParams.set('cat', state.cat);
  LOCATION_PARAMS.forEach(param => {
    const value = state[param];
    if (value) url.searchParams.set(param, value);
  });

  const entry = { ...state };
  if (push) window.history.pushState(entry, '', url);
  else window.history.replaceState(entry, '', url);
}

/**
 * Navigate to a view from a component that has no router callback of its own.
 *
 * Writes the location, then announces it the way the browser announces
 * back/forward, which is what `MainContent` and `App` both listen for — so the
 * screen changes through the same path a history step takes, not a second one.
 */
export function navigateToView(
  view: string,
  ids: LocationIds = {},
  cat: string | null = null
): void {
  if (typeof window === 'undefined') return;
  writeLocation({ view, cat, ...ids }, { push: true });
  window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
}

/**
 * The URL for a view, as a string — the form `App.tsx` needs for links and for
 * `history.pushState`. Views with a real path get it; everything else gets a
 * `?view=` query.
 */
export function urlForView(view: string, ids: LocationIds = {}): string {
  const path = VIEW_TO_PATH[view];
  if (path) return path;

  const params = new URLSearchParams();
  if (view) params.set('view', view);
  LOCATION_PARAMS.forEach(param => {
    const value = ids[param];
    if (value) params.set(param, String(value));
  });

  const qs = params.toString();
  return qs ? `/?${qs}` : '/';
}

/**
 * Legacy `#/hash-path` aliases, e.g. `#/orgchart` → `OrgChart`.
 *
 * These predate query-string routing and are kept because they are pasted into
 * emails and bookmarks. Nothing generates them any more, so the map only ever
 * needs entries removed, never added — a new screen gets a `?view=` URL.
 */
export const HASH_PATH_TO_VIEW: Readonly<Record<string, string>> = {
  home: 'Home',
  orgchart: 'OrgChart',
  rosters: 'Rosters',
  clients: 'ClientsList',
  'client-invoice-list': 'ClientInvoiceList',
  'client-invoices': 'ClientInvoiceList',
  'client-invoice-form': 'ClientInvoiceForm',
  'client-invoice-detail': 'ClientInvoiceDetail',
  'client-detail': 'ClientDetail',
  'client-request-list': 'ClientRequestList',
  'client-request': 'ClientRequest',
  'client-request-submitted': 'ClientRequestSubmitted',
  'crm-dashboard': 'CRMDashboard',
  crm: 'CRMDashboard',
  'pipeline-clients': 'PipelineClients',
  'top-opportunities': 'TopOpportunities',
  jobs: 'JobList',
  'job-list': 'JobList',
  'job-form': 'JobForm',
  'job-detail': 'JobDetail',
  rates: 'Rates',
  'rate-detail': 'RateDetail',
  expenses: 'ExpenseList',
  'expense-list': 'ExpenseList',
  'expense-form': 'ExpenseEntryForm',
  'expense-entry': 'ExpenseEntryForm',
  'expense-detail': 'ExpenseDetail',
  'expense-approvals': 'ExpenseApprovals',
  'expense-admin': 'ExpenseAdmin',
  'shared-card-ai-log': 'SharedCardAiLog',
  'receipt-auto-match': 'ReceiptAutoMatchDashboard',
  'expenses-dashboard': 'ExpensesDashboard',
  'procurement-estimates': 'ProcurementEstimatesList',
  'procurement-estimate-detail': 'ProcurementEstimateDetail',
  'estimate-submitted': 'EstimateSubmitted',
  requisitions: 'PurchaseOrdersTabs',
  'requisition-list': 'PurchaseOrdersTabs',
  'purchase-orders': 'PurchaseOrdersTabs',
  'purchase-order-list': 'PurchaseOrdersTabs',
  'procurement-jobs': 'ProcurementJobs',
  'purchase-order-approvals': 'PurchaseOrderApprovals',
  'po-approvals': 'PurchaseOrderApprovals',
  'requisition-form': 'RequisitionEntryForm',
  'requisition-detail': 'RequisitionDetail',
  'approved-vendors': 'ApprovedVendorList',
  'approved-vendor-detail': 'ApprovedVendorDetail',
  'dormant-vendors': 'DormantVendors',
  'hr-payslips': 'HRPayslips',
  payslips: 'HRPayslips',
  'hr-work-items': 'HRWorkItems',
  'hr-onboarding': 'HrOnboardingList',
  'hr-onboarding-form': 'HrOnboardingForm',
  'mechanical-movements': 'MechanicalMovementsList',
  'mechanical-movement-detail': 'MechanicalMovementDetail',
  'mechanical-new-booking': 'MechanicalNewBooking',
  'mechanical-scan': 'MechanicalScan',
  'mechanical-item-detail': 'MechanicalItemDetail',
  'psv-dashboard': 'PSVDashboard',
  psv: 'PSVDashboard',
  'psv-report-list': 'PSVReportList',
  'psv-report-form': 'PSVReportForm',
  'psv-report-detail': 'PSVReportDetail',
  'psv-config': 'PSVConfigPage',
  'psv-import': 'PSVImportPage',
  training: 'TrainingCompetencyDashboard',
  'training-dashboard': 'TrainingCompetencyDashboard',
  'competency-admin': 'CompetencyAdmin',
  'certificate-approvals': 'CertificateApprovalQueue',
  'my-training': 'MyTrainingProfile',
  // 'competency-matrices': archived — use MatrixConfigAdmin + MatrixEditor
  'user-management': 'UserManagement',
  'user-profile': 'UserProfile',
  'user-settings': 'UserSettings',
  'manual-of-authority': 'ManualOfAuthority',
  moa: 'ManualOfAuthority',
  'org-site-mapping': 'OrgSiteMappingAdmin',
};

/** What `parseUrl` could work out about the current address. */
export interface ParsedUrl {
  /** From the `/estimates/:id` detail path. */
  urlEstimateId: string | null;
  /** From the `/client-invoices/:id` detail path. */
  urlInvoiceId: string | null;
  view: string | null;
  ids: LocationIds & { cat?: string };
  path: string;
}

/**
 * Parse the current address into the shape `App.tsx` hydrates its state from.
 *
 * This is the wider, legacy-aware sibling of `readLocation()`: it also
 * recognises the two `/:id` detail paths and the `#/hash-path` aliases above.
 * `readLocation()` is what the router uses for ordinary navigation; this runs
 * on first load and on back/forward at the App level.
 *
 * Both now read the same `LOCATION_PARAMS` and the same `PATH_TO_VIEW`, which
 * is the point of moving this here — `App.tsx` previously carried its own
 * fourth copy of the deep-link map and a narrower list of id parameters, so a
 * parameter added to one reader was silently dropped by the other.
 */
export function parseUrl(): ParsedUrl {
  const out: ParsedUrl = {
    urlEstimateId: null,
    urlInvoiceId: null,
    view: null,
    ids: {},
    path: typeof window === 'undefined' ? '/' : window.location.pathname,
  };
  if (typeof window === 'undefined') return out;

  const estimateMatch = out.path.match(/^\/estimates\/([A-Za-z0-9]+)/);
  if (estimateMatch?.[1]) out.urlEstimateId = estimateMatch[1];

  const invoiceMatch = out.path.match(/^\/client-invoices\/([A-Za-z0-9]+)/);
  if (invoiceMatch?.[1]) out.urlInvoiceId = invoiceMatch[1];

  /** Collect the view and every known id from one parameter source. */
  const collect = (params: URLSearchParams): boolean => {
    const view = params.get('view');
    if (!view) return false;
    out.view = resolveLegacyHomeView(view, params.get('tab'));
    const cat = params.get('cat');
    if (cat) out.ids.cat = cat;
    LOCATION_PARAMS.forEach(param => {
      const value = params.get(param);
      if (value) out.ids[param] = value;
    });
    return true;
  };

  const hash = (window.location.hash || '').replace(/^#/, '');

  if (!collect(new URLSearchParams(window.location.search)) && hash) {
    if (!collect(new URLSearchParams(hash))) {
      // Not `#view=...`, so try the legacy `#/path` aliases.
      const hashPath = hash.replace(/^\/?/, '').toLowerCase();
      out.view = HASH_PATH_TO_VIEW[hashPath] ?? null;
    }
  }

  // Finally fall back to a real deep-link path. The legacy redirect applies
  // here too, exactly as in `readLocation()`: `/?tab=approvals` names no view,
  // reads as Home through the path map, and is a pre-split approvals link.
  if (!out.view) {
    out.view = resolveLegacyHomeView(
      PATH_TO_VIEW[out.path] ?? null,
      new URLSearchParams(window.location.search).get('tab')
    );
  }

  return out;
}

/**
 * Manage Rosters opened on one employee's swing — the Configuration tab's
 * "Amend swing": `?view=RostersAdmin&site=&date=&q=` (site, the swing's first
 * day, the employee's name as the planner's search).
 */
export function rosterAmendPath(s: { siteCode: string; start: string; employeeName: string }): string {
  return `/?${new URLSearchParams({ view: 'RostersAdmin', site: s.siteCode, date: s.start, q: s.employeeName })}`;
}

/** The planner's half of rosterAmendPath: what to open on. */
export function readRosterPlannerLink(search: string = window.location.search): { site?: string; date?: Date; q: string } {
  const p = new URLSearchParams(search);
  const date = p.get('date') || '';
  return {
    site: p.get('site') || undefined,
    date: /^\d{4}-\d{2}-\d{2}$/.test(date) ? new Date(`${date}T00:00:00`) : undefined,
    q: p.get('q') || '',
  };
}
